import { create } from "zustand";
import { persist } from "zustand/middleware";

type ChatConfig = {
  selectedToolIndex: number;
  isChatToolVisible: boolean;
  selectedModel: string;
  chatSettings: {
    model: string;
    modelProvider: string;
    instructions: string;
    reasoningEnabled: boolean;
    temperature: number;
    maxTokens: number;
  };
  toggleChatTool: () => void;
  setChatToolVisible: (visible: boolean) => void;
  setSelectedToolIndex: (index: number) => void;
  setSelectedModel: (model: string) => void;
  setChatSettings: (settings: Partial<ChatConfig['chatSettings']>) => void;
  getChatSettings: () => ChatConfig['chatSettings'];
};

export const useChatConfig = create<ChatConfig>()(
  persist(
    (set, get) => ({
      isChatToolVisible: false,
      selectedToolIndex: -1,
      selectedModel: "openai/gpt-4o-mini",
      chatSettings: {
        model: "openai/gpt-4o-mini",
        modelProvider: "OPEN_ROUTER",
        instructions: "",
        reasoningEnabled: false,
        temperature: 0.7,
        maxTokens: 1000,
      },
      toggleChatTool: () =>
        set((state) => ({ isChatToolVisible: !state.isChatToolVisible })),
      setChatToolVisible: (visible) => set({ isChatToolVisible: visible }),
      setSelectedToolIndex: (index) => set({ selectedToolIndex: index }),
      setSelectedModel: (model) => set({ selectedModel: model }),
      setChatSettings: (settings) =>
        set((state) => ({
          chatSettings: { 
            ...state.chatSettings, 
            ...settings,
            // Ensure modelProvider has a default value if not provided
            modelProvider: settings.modelProvider || state.chatSettings.modelProvider || 'unknown'
          },
          selectedModel: settings.model || state.selectedModel,
        })),
      getChatSettings: () => {
        const state = get();
        return {
          ...state.chatSettings,
          // Ensure modelProvider is always present
          modelProvider: state.chatSettings.modelProvider || 'unknown'
        };
      },
    }),
    {
      name: "chat-config",
      partialize: (state) => ({
        selectedModel: state.selectedModel,
        chatSettings: state.chatSettings,
      }),
      // Add migration logic for existing stored data
      migrate: (persistedState: any) => {
        if (persistedState && persistedState.chatSettings && !persistedState.chatSettings.modelProvider) {
          persistedState.chatSettings.modelProvider = 'unknown';
        }
        return persistedState;
      },
      version: 1,
    }
  )
);